export type SupportedCurrency = 'CHF' | 'EUR' | 'USD';

const normalizeCurrency = (
  currency: string | undefined | null,
): SupportedCurrency | null => {
  const code = (currency || '').toUpperCase();
  if (code === 'CHF' || code === 'EUR' || code === 'USD') return code;
  return null;
};

const CURRENCY_INCREMENT: Record<SupportedCurrency, number> = {
  CHF: 0.05,
  EUR: 0.01,
  USD: 0.01,
};

const CURRENCY_LOCALE: Record<SupportedCurrency, string> = {
  CHF: 'de-CH',
  EUR: 'de-DE',
  USD: 'en-US',
};

const getIncrement = (currency: string | undefined | null) => {
  const code = normalizeCurrency(currency);
  return code ? CURRENCY_INCREMENT[code] : 0.01;
};

const getLocale = (currency: string | undefined | null) => {
  const code = normalizeCurrency(currency);
  return code ? CURRENCY_LOCALE[code] : 'en-US';
};

/** Returns the forced locale used for money formatting of this currency. */
export const getMoneyLocale = (currency: string | undefined | null) =>
  getLocale(currency);

/**
 * Rounds `value` UP to the smallest cash denomination of `currency`.
 * This is display-only rounding (we never want to under-quote).
 */
export const roundUpToCurrencyIncrement = (
  currency: string,
  value: number,
): number => {
  const inc = getIncrement(currency);
  const factor = Math.round(1 / inc); // 20 for 0.05, 100 for 0.01
  // Avoid IEEE-754 drift nudging exact-grid values up (e.g. 0.15 becoming 0.1500000002).
  const rounded = Math.ceil(value * factor - 1e-9) / factor;
  // Intl formats negative zero as "-0" / "-0.00"; treat as plain zero.
  return rounded === 0 ? 0 : rounded;
};

const isEffectivelyInteger = (n: number) => Math.abs(n - Math.round(n)) < 1e-9;

/**
 * Amount-only formatter (no currency symbol), for UIs that render the symbol separately.
 * Uses a forced per-currency locale to get the expected grouping, e.g. CHF `2’450`.
 */
export const formatMoneyAmount = (currency: string, value: number): string => {
  const locale = getLocale(currency);
  const rounded = roundUpToCurrencyIncrement(currency, value);
  const isInt = isEffectivelyInteger(rounded);
  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(rounded);
};

/** Prefix used in the hero plan card (kept for layout consistency). */
export const planPriceLeadingSymbol = (currency: string) => {
  const code = (currency || '').toUpperCase();
  if (code === 'EUR') return '€';
  if (code === 'CHF') return 'CHF ';
  return '$';
};

/** Full money string including currency, grouped and rounded UP. */
export const formatMoney = (currency: string, value: number): string => {
  const code = normalizeCurrency(currency) ?? 'USD';
  const locale = getLocale(code);
  const rounded = roundUpToCurrencyIncrement(code, value);
  const isInt = isEffectivelyInteger(rounded);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    useGrouping: true,
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(rounded);
};
